import React, { useEffect, useState } from 'react';
import { Box, Flex, Text, Input, Switch, Select } from '@chakra-ui/react';
import { FilterDrawer } from '../../../../components/FilterDrawer';
import { IFiltrosAvancados, SentidoNota, TipoDoc } from '../../../../hooks/useConferenciaFiscal';

// Cores das fontes (mesmos tokens da tela) pros chips de tipo de documento.
const TIPO_COR: Record<TipoDoc, string> = {
  nfe: '#1B88F5',
  nfce: '#1B88F5',
  cte: '#6d28d9',
  nfse: '#6d28d9',
};
const TIPOS: Array<{ v: TipoDoc; lab: string }> = [
  { v: 'nfe', lab: 'NF-e' },
  { v: 'nfce', lab: 'NFC-e' },
  { v: 'cte', lab: 'CT-e' },
  { v: 'nfse', lab: 'NFS-e' },
];
// Atalhos de faixa de R$ divergente (o slider não serve pra cauda longa de valor;
// chip preenche os inputs, editar o input desmarca o chip — uma fonte de verdade).
const FAIXAS_RS: Array<{ lab: string; min?: number; max?: number }> = [
  { lab: '> 10 mil', min: 10000 },
  { lab: '10–50 mil', min: 10000, max: 50000 },
  { lab: '> 50 mil', min: 50000 },
];

const LABEL = {
  fontSize: '10px',
  fontWeight: 700,
  letterSpacing: '0.08em',
  color: '#94A3B8',
  textTransform: 'uppercase' as const,
};

const Toggle = ({
  label,
  dica,
  checked,
  onChange,
}: {
  label: string;
  dica: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) => (
  <Flex align="center" justify="space-between" gap={3} py={1}>
    <Box>
      <Text fontSize="13.5px" fontWeight={600} color="#334155">
        {label}
      </Text>
      <Text fontSize="11px" color="#94A3B8" lineHeight="1.3">
        {dica}
      </Text>
    </Box>
    <Switch
      isChecked={checked}
      onChange={e => onChange(e.target.checked)}
      colorScheme="blue"
      flexShrink={0}
    />
  </Flex>
);

const Chip = ({
  ativo,
  cor,
  onClick,
  children,
}: {
  ativo: boolean;
  cor: string;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <Box
    as="button"
    type="button"
    onClick={onClick}
    px="11px"
    py="6px"
    borderRadius="9px"
    border="1.5px solid"
    fontSize="12px"
    fontWeight={700}
    borderColor={ativo ? cor : 'rgba(15,23,42,0.1)'}
    bg={ativo ? `${cor}18` : 'transparent'}
    color={ativo ? cor : '#64748B'}
    _hover={{ borderColor: cor }}
    transition="all 0.14s"
  >
    {children}
  </Box>
);

const Secao = ({ titulo, children }: { titulo: string; children: React.ReactNode }) => (
  <Box mb={5}>
    <Text {...LABEL} mb={2}>
      {titulo}
    </Text>
    {children}
  </Box>
);

interface Props {
  isOpen: boolean;
  onClose: () => void;
  aplicado: IFiltrosAvancados;
  ufsDisponiveis: string[];
  onApply: (f: IFiltrosAvancados) => void;
  onClear: () => void;
}

/** Drawer de filtros avançados da Conferência. Mantém um RASCUNHO local; só
 *  aplica no "Filtrar" (evita refazer query a cada dígito de faixa). */
export const FiltrosAvancados = ({
  isOpen,
  onClose,
  aplicado,
  ufsDisponiveis,
  onApply,
  onClear,
}: Props) => {
  const [draft, setDraft] = useState<IFiltrosAvancados>(aplicado);
  // ao abrir, o rascunho parte do que está aplicado
  useEffect(() => {
    if (isOpen) setDraft(aplicado);
  }, [isOpen, aplicado]);

  const set = (patch: Partial<IFiltrosAvancados>) => setDraft(d => ({ ...d, ...patch }));
  const toggleDoc = (v: TipoDoc) => {
    const cur = draft.doc ?? [];
    set({ doc: cur.includes(v) ? cur.filter(x => x !== v) : [...cur, v] });
  };
  const toggleUf = (u: string) => {
    const cur = draft.uf ?? [];
    set({ uf: cur.includes(u) ? cur.filter(x => x !== u) : [...cur, u] });
  };
  const faixaAtiva = (f: (typeof FAIXAS_RS)[number]) =>
    draft.r_min === f.min && draft.r_max === f.max;

  const temAlgum = Object.values(draft).some(v =>
    Array.isArray(v) ? v.length > 0 : v !== undefined && v !== false,
  );

  return (
    <FilterDrawer
      isOpen={isOpen}
      onClose={onClose}
      title="Filtros avançados"
      hasActiveFilters={temAlgum}
      onClearFilters={() => {
        setDraft({});
        onClear();
      }}
      onApplyFilters={() => onApply(draft)}
    >
      <Secao titulo="Situação">
        <Toggle
          label="Só o que dá trabalho"
          dica="Esconde Conferido e Sem dados — deixa só o acionável."
          checked={!!draft.acionaveis}
          onChange={v => set({ acionaveis: v || undefined })}
        />
        <Toggle
          label="Situação divergente"
          dica="Notas casadas com cancelamento só num lado — o falso verde."
          checked={!!draft.sit_div}
          onChange={v => set({ sit_div: v || undefined })}
        />
        <Toggle
          label="Portal viu, robô não"
          dica="Portal trouxe mais que o Autmais — robô com ponto cego que não acusa erro."
          checked={!!draft.portal_cego}
          onChange={v => set({ portal_cego: v || undefined })}
        />
      </Secao>

      <Secao titulo="Sentido da divergência">
        <Flex gap={2}>
          {(['entrada', 'saida'] as SentidoNota[]).map(s => (
            <Chip
              key={s}
              ativo={draft.sentido === s}
              cor="#1B88F5"
              onClick={() => set({ sentido: draft.sentido === s ? undefined : s })}
            >
              {s === 'entrada' ? 'Entrada' : 'Saída'}
            </Chip>
          ))}
        </Flex>
      </Secao>

      <Secao titulo="Documento com divergência">
        <Flex gap={2} wrap="wrap">
          {TIPOS.map(t => (
            <Chip
              key={t.v}
              ativo={(draft.doc ?? []).includes(t.v)}
              cor={TIPO_COR[t.v]}
              onClick={() => toggleDoc(t.v)}
            >
              {t.lab}
            </Chip>
          ))}
        </Flex>
      </Secao>

      <Secao titulo="R$ divergente">
        <Flex gap={2} wrap="wrap" mb={3}>
          {FAIXAS_RS.map(f => (
            <Chip
              key={f.lab}
              ativo={faixaAtiva(f)}
              cor="#b91c1c"
              onClick={() =>
                faixaAtiva(f)
                  ? set({ r_min: undefined, r_max: undefined })
                  : set({ r_min: f.min, r_max: f.max })
              }
            >
              {f.lab}
            </Chip>
          ))}
        </Flex>
        <Flex gap={2} align="center">
          <Input
            size="sm"
            type="number"
            placeholder="De R$"
            value={draft.r_min ?? ''}
            onChange={e => set({ r_min: e.target.value ? Number(e.target.value) : undefined })}
          />
          <Text color="#94A3B8">até</Text>
          <Input
            size="sm"
            type="number"
            placeholder="R$"
            value={draft.r_max ?? ''}
            onChange={e => set({ r_max: e.target.value ? Number(e.target.value) : undefined })}
          />
        </Flex>
      </Secao>

      <Secao titulo="Localização">
        <Select
          size="sm"
          placeholder="Adicionar UF…"
          value=""
          onChange={e => e.target.value && toggleUf(e.target.value)}
        >
          {ufsDisponiveis
            .filter(u => !(draft.uf ?? []).includes(u))
            .map(u => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
        </Select>
        {(draft.uf ?? []).length > 0 && (
          <Flex gap={2} wrap="wrap" mt={2}>
            {(draft.uf ?? []).map(u => (
              <Chip key={u} ativo cor="#047857" onClick={() => toggleUf(u)}>
                {u} ✕
              </Chip>
            ))}
          </Flex>
        )}
      </Secao>
    </FilterDrawer>
  );
};
